import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import {
  X,
  Download,
  Eye,
  Send,
  Save,
  Loader,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import ContractTemplate from '../contracts/ContractTemplate';

// Convert "10:40 AM" → "10:40" for <input type="time"> (needs 24h)
const to24h = (timeStr) => {
  if (!timeStr) return '';
  if (!/AM|PM/i.test(timeStr)) return timeStr; // already 24h
  const [time, meridiem] = timeStr.trim().split(/\s+/);
  let [hours, minutes] = time.split(':').map(Number);
  if (meridiem.toUpperCase() === 'AM') {
    if (hours === 12) hours = 0;
  } else {
    if (hours !== 12) hours += 12;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

// Convert "10:40" (24h) → "10:40 AM" for storage
const to12h = (timeStr) => {
  if (!timeStr) return '';
  if (/AM|PM/i.test(timeStr)) return timeStr; // already 12h
  const [hours, minutes] = timeStr.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

export default function ContractEditModal({ booking, contract: existingContract, onClose, onSend, onSaveDraft }) {
  const [contract, setContract] = useState({
    // Use existing contract data if available, otherwise use booking data
    id: existingContract?.id || null,

    // Client info
    clientName: existingContract?.clientName || booking.contactName,
    clientEmail: existingContract?.clientEmail || booking.contactEmail,
    clientPhone: existingContract?.clientPhone || booking.contactPhone,
    organization: existingContract?.organization || booking.organization || null,

    // Event details
    eventDate: existingContract?.eventDate || booking.eventDate,
    eventTime: existingContract?.eventTime || booking.eventTime,
    eventEndTime: existingContract?.eventEndTime || null,
    eventType: existingContract?.eventType || booking.eventType,
    venue: existingContract?.venue || booking.venue,
    venueAddress: existingContract?.venueAddress || (booking.address
      ? `${booking.address}, ${booking.city || ''}, ${booking.state || ''}`
      : null),
    guestCount: existingContract?.guestCount || booking.guestCount || null,

    // Artist & Package
    artistId: existingContract?.artistId || booking.artist,
    artistName: existingContract?.artistName || booking.artistName,
    packageId: existingContract?.packageId || booking.package,
    packageName: existingContract?.packageName || booking.packageName,
    packageDuration: existingContract?.packageDuration || booking.packageDuration,
    // Financial - leave empty for admin to enter manually
    packagePrice: existingContract?.packagePrice || null,
    depositAmount: existingContract?.depositAmount ?? 0,
    totalAmount: existingContract?.totalAmount ?? 0,
    secondPaymentAmount: existingContract?.secondPaymentAmount ?? 0,
    secondPaymentDue: existingContract?.secondPaymentDue || '',

    // Production times
    loadInTime: existingContract?.loadInTime || null,
    soundCheckTime: existingContract?.soundCheckTime || null,

    // Sound & Backline
    requiresSound: existingContract?.requiresSound || 'no',
    soundBacklineCost: existingContract?.soundBacklineCost || 0,

    // Special Addendum
    addendum: existingContract?.addendum || '',
  });

  const [showPreview, setShowPreview] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [showSecondPayment, setShowSecondPayment] = useState(!!(existingContract?.secondPaymentAmount));
  const [isSending, setIsSending] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [sendError, setSendError] = useState(null);

  const handleContractChange = (field, value) => {
    setContract((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    setSendError(null);
    setDraftSaved(false);

    try {
      await onSaveDraft(contract);
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch (error) {
      setSendError(error.message || 'Failed to save draft');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSendContract = async () => {
    setIsSending(true);
    setSendError(null);

    try {
      await onSend(contract);
    } catch (error) {
      setSendError(error.message || 'Failed to send contract');
    } finally {
      setIsSending(false);
    }
  };

  // Full screen PDF preview modal
  if (showPreview) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col"
      >
        <div className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Contract Preview</h2>
          <Button
            onClick={() => setShowPreview(false)}
            variant="outline"
            className="gap-2"
          >
            <X className="w-4 h-4" />
            Close Preview
          </Button>
        </div>
        <div className="flex-1 p-4">
          <PDFViewer key={previewKey} width="100%" height="100%" className="rounded-lg">
            <ContractTemplate contract={contract} />
          </PDFViewer>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-white">Edit Contract - {contract.clientName}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {sendError && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg flex gap-3 items-start">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-red-300">Error sending contract</p>
                <p className="text-red-200 text-sm">{sendError}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Edit Form */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4 text-white">Contract Terms</h3>
              </div>

              <div>
                <Label className="text-white">Client Name</Label>
                <Input
                  value={contract.clientName}
                  onChange={(e) => handleContractChange('clientName', e.target.value)}
                  placeholder="Client/Purchaser name"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Client Email</Label>
                <Input
                  value={contract.clientEmail}
                  onChange={(e) => handleContractChange('clientEmail', e.target.value)}
                  placeholder="Client email address"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Client Phone</Label>
                <Input
                  value={contract.clientPhone}
                  onChange={(e) => handleContractChange('clientPhone', e.target.value)}
                  placeholder="Client phone number"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Organization</Label>
                <Input
                  value={contract.organization || ''}
                  onChange={(e) => handleContractChange('organization', e.target.value)}
                  placeholder="Organization (optional)"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Artist Name</Label>
                <Input
                  value={contract.artistName || ''}
                  onChange={(e) => handleContractChange('artistName', e.target.value)}
                  placeholder="Artist/Band name"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Package Name</Label>
                <Input
                  value={contract.packageName || ''}
                  onChange={(e) => handleContractChange('packageName', e.target.value)}
                  placeholder="Package name"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Event Type</Label>
                <select
                  value={contract.eventType || ''}
                  onChange={(e) => handleContractChange('eventType', e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2"
                >
                  <option value="">Select event type</option>
                  <option value="wedding">Wedding</option>
                  <option value="corporate">Corporate Event</option>
                  <option value="birthday">Birthday Party</option>
                  <option value="club">Club/Venue</option>
                  <option value="festival">Festival</option>
                  <option value="private_party">Private Party</option>
                  <option value="concert">Concert</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <Label className="text-white">Venue</Label>
                <Input
                  value={contract.venue}
                  onChange={(e) => handleContractChange('venue', e.target.value)}
                  placeholder="Venue name"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Venue Address</Label>
                <Input
                  value={contract.venueAddress || ''}
                  onChange={(e) => handleContractChange('venueAddress', e.target.value)}
                  placeholder="Full venue address"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Package Duration</Label>
                <Input
                  value={contract.packageDuration || ''}
                  onChange={(e) => handleContractChange('packageDuration', e.target.value)}
                  placeholder="e.g., 2 Hours"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Event Date</Label>
                <Input
                  type="date"
                  value={contract.eventDate || ''}
                  onChange={(e) => handleContractChange('eventDate', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Event Start Time</Label>
                <Input
                  type="time"
                  value={to24h(contract.eventTime)}
                  onChange={(e) => handleContractChange('eventTime', to12h(e.target.value))}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Event End Time</Label>
                <Input
                  type="time"
                  value={to24h(contract.eventEndTime)}
                  onChange={(e) => handleContractChange('eventEndTime', to12h(e.target.value))}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Load In Time</Label>
                <Input
                  type="time"
                  value={to24h(contract.loadInTime)}
                  onChange={(e) => handleContractChange('loadInTime', to12h(e.target.value))}
                  placeholder="Load in time for crew"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Sound Check Time</Label>
                <Input
                  type="time"
                  value={to24h(contract.soundCheckTime)}
                  onChange={(e) => handleContractChange('soundCheckTime', to12h(e.target.value))}
                  placeholder="Sound check deadline"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Total Amount ($)</Label>
                <Input
                  type="number"
                  value={contract.totalAmount}
                  onChange={(e) => handleContractChange('totalAmount', Number(e.target.value))}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <Label className="text-white">Deposit Amount ($)</Label>
                <Input
                  type="number"
                  value={contract.depositAmount}
                  onChange={(e) => handleContractChange('depositAmount', Number(e.target.value))}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              {/* Optional Second Payment */}
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setShowSecondPayment(!showSecondPayment);
                    if (showSecondPayment) {
                      handleContractChange('secondPaymentAmount', '');
                      handleContractChange('secondPaymentDue', '');
                    }
                  }}
                  className="text-sm text-orange-400 hover:text-orange-300 underline"
                >
                  {showSecondPayment ? '- Remove Second Payment' : '+ Add Second Payment'}
                </button>
              </div>

              {showSecondPayment && (
                <>
                  <div>
                    <Label className="text-white">Second Payment Amount ($)</Label>
                    <Input
                      type="number"
                      value={contract.secondPaymentAmount}
                      onChange={(e) => handleContractChange('secondPaymentAmount', Number(e.target.value))}
                      placeholder="e.g., 500"
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Second Payment Due</Label>
                    <Input
                      value={contract.secondPaymentDue}
                      onChange={(e) => handleContractChange('secondPaymentDue', e.target.value)}
                      placeholder="e.g., due 30 days before event"
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div className="text-xs text-gray-400">
                    Final balance: ${((contract.totalAmount || 0) - (contract.depositAmount || 0) - (contract.secondPaymentAmount || 0)).toLocaleString()}
                  </div>
                </>
              )}

              {/* Sound & Backline Section */}
              <div className="pt-4 border-t border-gray-700">
                <h4 className="text-md font-semibold mb-4 text-orange-400">Sound & Backline</h4>
              </div>

              <div>
                <Label className="text-white">Will you require audio/sound equipment and backline?</Label>
                <select
                  value={contract.requiresSound}
                  onChange={(e) => handleContractChange('requiresSound', e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                  <option value="not_sure">Not Sure</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Includes mixing console, monitors, microphones, DI boxes, drums, keyboards, amps, etc.
                </p>
              </div>

              {contract.requiresSound === 'yes' && (
                <div>
                  <Label className="text-white">Sound & Backline Cost ($)</Label>
                  <Input
                    type="number"
                    value={contract.soundBacklineCost}
                    onChange={(e) => handleContractChange('soundBacklineCost', Number(e.target.value))}
                    placeholder="Enter cost for sound equipment"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              )}

              {/* Special Addendum */}
              <div className="pt-4 border-t border-gray-700">
                <h4 className="text-md font-semibold mb-4 text-orange-400">Special Addendum</h4>
              </div>

              <div>
                <Label className="text-white">Addendum Language (Optional)</Label>
                <textarea
                  value={contract.addendum || ''}
                  onChange={(e) => handleContractChange('addendum', e.target.value)}
                  rows={4}
                  placeholder="Add any special terms, modifications, or client-requested changes here. This will appear as a Special Addendum section in the contract."
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave blank to omit the addendum section from the contract.
                </p>
              </div>
            </div>

            {/* Preview Summary */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-white">Contract Summary</h3>
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 space-y-3">
                <div className="text-sm">
                  <div className="font-semibold text-orange-400">CLIENT</div>
                  <div className="text-gray-300">{contract.clientName}</div>
                  <div className="text-gray-400 text-xs">{contract.clientEmail}</div>
                  <div className="text-gray-400 text-xs">{contract.clientPhone}</div>
                </div>

                <div className="text-sm">
                  <div className="font-semibold text-orange-400">EVENT</div>
                  <div className="text-gray-300">{contract.venue}</div>
                  <div className="text-gray-400 text-xs">
                    {contract.eventDate
                      ? (() => {
                          try {
                            if (/^\d{4}-\d{2}-\d{2}$/.test(contract.eventDate)) {
                              const [y, m, d] = contract.eventDate.split('-').map(Number);
                              return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                            }
                            return contract.eventDate;
                          } catch { return contract.eventDate; }
                        })()
                      : 'TBD'}{' '}
                    {contract.eventTime && contract.eventEndTime
                      ? `from ${to12h(contract.eventTime)} to ${to12h(contract.eventEndTime)}`
                      : contract.eventTime
                        ? `at ${to12h(contract.eventTime)}`
                        : ''}
                  </div>
                  <div className="text-gray-400 text-xs">{contract.packageDuration}</div>
                </div>

                <div className="text-sm">
                  <div className="font-semibold text-orange-400">PACKAGE</div>
                  <div className="text-gray-300">{contract.artistName} - {contract.packageName}</div>
                </div>

                <div className="text-sm">
                  <div className="font-semibold text-orange-400">FINANCIAL</div>
                  <div className="text-gray-300">Total: ${contract.totalAmount?.toLocaleString()}</div>
                  <div className="text-gray-400 text-xs">Deposit: ${contract.depositAmount?.toLocaleString()}</div>
                  {contract.secondPaymentAmount > 0 && (
                    <div className="text-gray-400 text-xs">
                      Second Payment: ${contract.secondPaymentAmount?.toLocaleString()}
                      {contract.secondPaymentDue && ` (${contract.secondPaymentDue})`}
                    </div>
                  )}
                  <div className="text-gray-400 text-xs">
                    Remaining Balance: ${((contract.totalAmount || 0) - (contract.depositAmount || 0) - (contract.secondPaymentAmount || 0)).toLocaleString()}
                  </div>
                  {contract.requiresSound === 'yes' && contract.soundBacklineCost > 0 && (
                    <div className="text-gray-400 text-xs">Sound & Backline: ${contract.soundBacklineCost?.toLocaleString()}</div>
                  )}
                </div>

                <div className="text-sm">
                  <div className="font-semibold text-orange-400">SOUND & BACKLINE</div>
                  <div className="text-gray-300">
                    Required: {contract.requiresSound === 'yes' ? 'Yes' : contract.requiresSound === 'not_sure' ? 'Not Sure' : 'No'}
                  </div>
                  {contract.requiresSound === 'yes' && contract.soundBacklineCost > 0 && (
                    <div className="text-gray-400 text-xs">Cost: ${contract.soundBacklineCost?.toLocaleString()}</div>
                  )}
                </div>

                {contract.addendum && (
                  <div className="text-sm">
                    <div className="font-semibold text-orange-400">SPECIAL ADDENDUM</div>
                    <div className="text-gray-300 text-xs whitespace-pre-wrap">{contract.addendum}</div>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-gray-700">
                  <p className="text-xs text-gray-400">Click "Preview PDF" to see the full contract document</p>
                </div>
              </div>

              <div className="mt-4 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <p className="text-sm text-orange-300">
                  <strong>Note:</strong> When you send this contract, the customer will see these exact details on the signing page.
                </p>
              </div>
            </div>
          </div>

          {/* Additional editable fields */}
          <div className="mt-6 space-y-4">
            <div>
              <Label className="text-white">Guest Count (if applicable)</Label>
              <Input
                type="number"
                value={contract.guestCount || ''}
                onChange={(e) => handleContractChange('guestCount', e.target.value ? Number(e.target.value) : null)}
                placeholder="Expected number of guests"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-wrap gap-3 justify-end border-t border-gray-700 pt-6">
            <Button
              onClick={() => { setPreviewKey((k) => k + 1); setShowPreview(true); }}
              variant="outline"
              className="gap-2"
            >
              <Eye className="w-4 h-4" />
              Preview PDF
            </Button>

            <PDFDownloadLink
              document={<ContractTemplate contract={contract} />}
              fileName={`${contract.clientName}-Contract.pdf`}
            >
              <Button variant="outline" className="gap-2">
                <Download className="w-4 h-4" />
                Download PDF
              </Button>
            </PDFDownloadLink>

            <Button
              onClick={handleSaveDraft}
              disabled={isSavingDraft || isSending}
              variant="outline"
              className="gap-2"
            >
              {isSavingDraft ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : draftSaved ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Draft Saved
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save as Draft
                </>
              )}
            </Button>

            <Button
              onClick={handleSendContract}
              disabled={isSending || isSavingDraft}
              className="gap-2 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
            >
              {isSending ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Save & Send to Customer
                </>
              )}
            </Button>

            <Button
              onClick={onClose}
              variant="ghost"
              className="text-gray-400 hover:text-white"
            >
              Cancel
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
